import { ColumnDef, Row, Table } from '@tanstack/react-table';
import AlarmIcon from '../../assets/icons/alarm.svg?react';
import GraphIcon from '../../assets/icons/graph.svg?react';
import { Badge } from '../../components/Badge';
import { Checkbox } from '../../components/Checkbox';
import { TableRowActions } from '../../components/tables/TableRowActions';
import { Machine, MachineStatus } from '../../types/machines/machine.types';
import { openSingleMachineGraph } from '../../utils/graphNavigation';
import {
  formatStatus,
  MACHINE_COLUMN_CONFIGS,
  MachineColumnConfig,
} from './machineColumnConfig';
import { MachineStatusBadge } from './MachineStatusBadge';

export interface MachineColumnsOptions {
  selectedMachineIds: string[];
  onSelectionChange: (machineIds: string[]) => void;
  onAddAlarm?: (machineId: string) => void;
}

/**
 * Creates a standard text cell renderer
 */
function createTextCell(columnId: string, emptyText?: string) {
  return ({ row }: { row: { getValue: (id: string) => unknown } }) => {
    const value = row.getValue(columnId) as string;

    if (!value && emptyText) {
      return (
        <div className='text-secondary truncate italic' title={emptyText}>
          {emptyText}
        </div>
      );
    }

    return (
      <div className='truncate' title={value}>
        {value}
      </div>
    );
  };
}

/**
 * Creates a badge cell renderer for status columns.
 */
function createBadgeCell(
  columnId: string,
  variantMap: Record<
    string,
    'success' | 'warning' | 'error' | 'info' | 'neutral'
  >
) {
  return ({ row }: { row: Row<Machine> }) => {
    const value = row.getValue(columnId) as MachineStatus;
    const variant = variantMap[value] || 'neutral';

    return <Badge variant={variant}>{formatStatus(value)}</Badge>;
  };
}

/**
 * Creates the Machine state cell.
 *
 * The badge is EDMS's `state`, verbatim - Pulse substitutes nothing for it, so
 * a synthetic offline reads offline exactly as EDMS reports it. When EDMS says
 * the state is synthetic, the tooltip discloses that and names the last state
 * EDMS did record, which is usually the question the reader has next.
 */
function createStatusCell() {
  return ({ row }: { row: Row<Machine> }) => {
    const { status, statusIsSynthetic, lastRecordedStatus } = row.original;
    if (!statusIsSynthetic) {
      return <MachineStatusBadge status={status} />;
    }

    const lastReported = lastRecordedStatus
      ? ` Its last recorded state was ${formatStatus(lastRecordedStatus)}.`
      : '';

    return (
      <MachineStatusBadge
        status={status}
        title={
          `EDMS set this because it has not received a heartbeat from this ` +
          `machine recently, so it says nothing about the machine beyond ` +
          `"not heard from".${lastReported}`
        }
      />
    );
  };
}

/**
 * Generate a column definition from config
 */
function createColumnFromConfig(
  config: MachineColumnConfig
): ColumnDef<Machine> {
  const columnId = config.id;
  const accessorKey = config.accessorKey || config.id;

  let cell: ColumnDef<Machine>['cell'];
  if (config.cellType === 'status') {
    cell = createStatusCell();
  } else if (config.cellType === 'badge' && config.badgeVariants) {
    cell = createBadgeCell(columnId, config.badgeVariants);
  } else {
    cell = createTextCell(columnId, config.emptyText);
  }

  return {
    accessorKey,
    id: columnId,
    header: config.header,
    cell,
    enableSorting: config.sortable,
    enableHiding: config.canHide,
    enableResizing: config.resizable ?? true,
    enablePinning: !!config.pinned,
    size: config.size,
    minSize: config.minSize,
  };
}

/**
 * Creates machine table columns from configuration.
 * All columns defined in MACHINE_COLUMN_CONFIGS are included.
 * Visibility is controlled via columnVisibility state, not by column definitions.
 */
export function createMachineColumns({
  selectedMachineIds,
  onSelectionChange,
  onAddAlarm,
}: MachineColumnsOptions): ColumnDef<Machine>[] {
  // Selection column (pinned left)
  const selectColumn: ColumnDef<Machine> = {
    id: 'select',
    header: ({ table }: { table: Table<Machine> }) => {
      const currentPageIds = table
        .getRowModel()
        .rows.map((row) => row.original.id);

      const allCurrentPageSelected =
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => selectedMachineIds.includes(id));

      const someCurrentPageSelected = currentPageIds.some((id) =>
        selectedMachineIds.includes(id)
      );

      return (
        <div className='flex items-center justify-center'>
          <Checkbox
            checked={
              allCurrentPageSelected ||
              (someCurrentPageSelected && 'indeterminate')
            }
            onCheckedChange={(value) => {
              if (value) {
                const newIds = Array.from(
                  new Set([...selectedMachineIds, ...currentPageIds])
                );
                onSelectionChange(newIds);
              } else {
                onSelectionChange(
                  selectedMachineIds.filter(
                    (id) => !currentPageIds.includes(id)
                  )
                );
              }
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label='Select all'
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const isSelected = selectedMachineIds.includes(row.original.id);
      return (
        <div className='flex items-center justify-center'>
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => {
              if (value) {
                onSelectionChange([...selectedMachineIds, row.original.id]);
              } else {
                onSelectionChange(
                  selectedMachineIds.filter((id) => id !== row.original.id)
                );
              }
            }}
            onClick={(e) => e.stopPropagation()}
            aria-label='Select row'
          />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    enablePinning: true,
    size: 58,
    minSize: 58,
  };

  // Actions column (pinned right)
  const actionsColumn: ColumnDef<Machine> = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const machine = row.original;
      return (
        <div className='flex items-center justify-center'>
          <TableRowActions
            actions={[
              {
                label: 'Show graphs',
                onClick: () => openSingleMachineGraph(machine.machineId),
                icon: <GraphIcon />,
              },
              {
                label: 'Add Pulse alert',
                onClick: () => onAddAlarm?.(machine.id),
                separator: true,
                icon: <AlarmIcon />,
              },
            ]}
          />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    enablePinning: true,
    size: 56,
    minSize: 56,
  };

  // Generate data columns from configuration
  const dataColumns = MACHINE_COLUMN_CONFIGS.map(createColumnFromConfig);

  return [selectColumn, ...dataColumns, actionsColumn];
}
